<template>
  <div class="donation-campaign-orders-report">
    <div class="settings-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back banner-back-button" :title="$t('general.back')" href="#" @click.prevent="goBack">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
        <p class="donation-campaign-orders-report__title" v-if="campaignTitle">
          {{ campaignTitle }}
        </p>
      </div>
    </div>

    <div class="sales-report mar-to-15">
      <div class="col-md-12">
        <div class="card">
          <div class="card-content total-pagination pad-15">
            <data-tables-server
              @query-change="paginationChanged"
              v-loading="loading"
              :data="tableData"
              :search-def="{ show: false }"
              :total="total"
              :pagination-props="paginationProps"
              :table-props="tableOptions"
              style="width: 100%;"
            >
              <el-table-column :min-width="90" prop="order_id" :label="$t('reports.orderId')" sortable="custom">
                <template slot-scope="props">
                  {{ props.row.order_id || props.row.app_id || '-' }}
                </template>
              </el-table-column>

              <el-table-column :min-width="120" :label="$t('donationReport.client')">
                <template slot-scope="props">
                  {{ getClientName(props.row) }}
                </template>
              </el-table-column>

              <el-table-column :min-width="120" :label="$t('reports.email')">
                <template slot-scope="props">
                  {{ props.row.email || (props.row.user && props.row.user.email) || props.row.client_email || '-' }}
                </template>
              </el-table-column>

              <el-table-column :min-width="90" prop="amount" :label="$t('donationReport.amount')" sortable="custom">
                <template slot-scope="props">
                  <b>{{ parseFloat(props.row.amount || props.row.donation_amount || 0).toFixed(2) }} {{ currency }}</b>
                </template>
              </el-table-column>

              <el-table-column :min-width="100" :label="$t('reports.location')">
                <template slot-scope="props">
                  {{ getLocationName(props.row.location_id) || (props.row.location && props.row.location.name) || props.row.location_name || '-' }}
                </template>
              </el-table-column>

              <el-table-column :min-width="100" prop="credited_at" :label="$t('donationReport.date')" sortable="custom">
                <template slot-scope="props">
                  {{ formatOrderDate(props.row) }}
                </template>
              </el-table-column>

              <el-table-column :min-width="90" :label="$t('orders.status')">
                <template slot-scope="props">
                  {{ props.row.status || props.row.order_status || '-' }}
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import DataTables from 'vue-data-tables'
import swal from 'sweetalert2'

Vue.use(DataTables)

export default {
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: true,
      currency: this.getCurrencySymbol(this.$session.get('selectedCompanyData')['currency_iso']),
      locations: [],
      timezone: (this.$session.get('selectedCompanyData') || {}).timezone || 'Europe/Bucharest',
      tableData: [],
      total: 0,
      sortBy: 'credited_at',
      sortOrder: 'desc',
      paginationProps: {
        layout: 'prev, pager, next, sizes, total',
        currentPage: 1,
        pageSize: 25
      },
      tableOptions: {
        defaultSort: {
          prop: 'credited_at',
          order: 'descending'
        }
      }
    }
  },
  created () {
    this.locations = this.getCompanyDetail('locations_details') || []
  },
  computed: {
    campaignId () {
      const fromProp = this.route && this.route.params && this.route.params.campaignId
      const fromRouter = this.$route && this.$route.params && this.$route.params.campaignId
      return fromProp != null && fromProp !== '' ? fromProp : fromRouter
    },
    campaignTitle () {
      const q = (this.route && this.route.query) || (this.$route && this.$route.query) || {}
      return q.title || ''
    }
  },
  watch: {
    campaignId: {
      immediate: true,
      handler (id) {
        if (id != null && id !== '') {
          this.paginationProps.currentPage = 1
          this.getOrders()
        }
      }
    }
  },
  methods: {
    getClientName (row) {
      if (row.client_name) return row.client_name
      const name = [row.firstname, row.lastname].filter(Boolean).join(' ').trim()
      if (name) return name
      if (row.user) {
        return [row.user.firstname, row.user.lastname].filter(Boolean).join(' ').trim() || '-'
      }
      return '-'
    },
    getLocationName (locationId) {
      if (locationId === null || locationId === undefined || locationId === '') return ''
      const numericId = Number(locationId)
      const location = this.locations.find(loc =>
        loc.true_id === locationId ||
        loc.id === locationId ||
        (!Number.isNaN(numericId) && (loc.true_id === numericId || loc.id === numericId))
      )
      return location && location.name ? location.name : ''
    },
    getOrderDateValue (row) {
      if (row.credited_at && row.credited_at.date) return row.credited_at.date
      if (row.order_created_at && row.order_created_at.date) return row.order_created_at.date
      return row.created_at || row.date || null
    },
    formatOrderDate (row) {
      const date = this.getOrderDateValue(row)
      if (!date) return '-'
      const normalized = String(date).trim().replace(' ', 'T').replace(/\.\d+$/, '')
      return this.formatToCompanyTimezone(normalized, this.timezone)
    },
    paginationChanged (query) {
      if (query.type === 'page' || query.type === 'size' || query.type === 'sort') {
        this.getOrders(query)
      }
    },
    getOrders (query) {
      if (!this.campaignId) return

      let page = this.paginationProps.currentPage || 1
      let pageSize = this.paginationProps.pageSize || 25

      if (query) {
        if (query.page) page = query.page
        if (query.pageSize) pageSize = query.pageSize
        if (query.sort && query.sort.prop) {
          this.sortBy = query.sort.prop
          this.sortOrder = query.sort.order === 'descending' ? 'desc' : 'asc'
        }
      }

      const data = {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany'),
        page,
        per_page: pageSize,
        field: this.sortBy,
        order: this.sortOrder
      }

      this.loading = true
      this.apiRequest('/reports/donation-campaigns/' + this.campaignId + '/orders', 'GET', data, (response) => {
        if (response.status === true) {
          let items = []
          let pagination = null
          if (Array.isArray(response.data)) {
            items = response.data
          } else {
            const payload = response.data || {}
            items = payload.data || payload.items || []
            pagination = payload.pagination || null
          }
          this.tableData = items
          this.total = pagination
            ? (pagination.totalRecords || pagination.total || 0)
            : (response.total || items.length)
          this.paginationProps.currentPage = pagination
            ? (pagination.page || page)
            : page
          this.paginationProps.pageSize = pagination
            ? (pagination.per_page || pagination.perPage || pageSize)
            : pageSize
        } else {
          this.tableData = []
          this.total = 0
          swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
        }
        this.$nextTick(() => {
          this.loading = false
        })
      })
    },
    goBack () {
      this.$bus.$emit('refreshDonationCampaignsReport')
      this.$bus.$emit('close-modal')
    }
  }
}
</script>

<style scoped>
.donation-campaign-orders-report__title {
  margin: 0 0 0 12px;
  font-weight: 600;
  color: #25396F;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/DonationCampaignOrdersReport.vue